/**
 * Uses Swiper package to create a carousel for product media
 * More info: https://swiperjs.com//
 */
if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    constructor() {
      super();

      this.swiperInitialized = false;
      this.productModalHTML = {};
      this.selectors = {
        header: 'sticky-header',
        slider: '[data-slider]',
        thumbs: '[data-thumbs]',
        mediaItem: '[data-media-item]',
        mediaColor: 'data-media-color',
        thumbnail: '[data-thumbnail]'
      };
      this.filterMediaBy = this.dataset.galleryFilter;
      this.selectedMediaIndex =
        Number(
          this.querySelector(this.selectors.slider)?.querySelector(
            '[data-selected]'
          )?.dataset.index
        ) || 0;
      this.breakpoints = {
        desktop: 990,
        desktop_large: 1440
      };
      this.settings = {
        elements: {
          slider: this.querySelector(this.selectors.slider),
          thumbs: this.querySelector(this.selectors.thumbs)
        },
        instances: {
          slider: null,
          thumbs: null
        },
        options: {
          slider: {
            initialSlide: this.selectedMediaIndex,
            watchOverflow: true,
            watchSlidesVisibility: true,
            watchSlidesProgress: true,
            observer: true,
            observeParents: true,
            pagination: {
              el: '.swiper-pagination',
              type: 'progressbar'
            },
            autoHeight: this.hasAttribute('data-slider-autoheight'),
            breakpoints: {
              [this.breakpoints.desktop]: {
                allowTouchMove: false,
                pagination: false
              }
            }
          },
          thumbs: {
            initialSlide: this.selectedMediaIndex,
            // slidesPerView is always 'auto' — how many thumbs show is
            // purely a function of the CSS width you give each slide
            // (see #applyThumbWidths below), not a number Swiper enforces.
            slidesPerView: 'auto',
            spaceBetween: Number(
              this.getAttribute('data-thumbs-gap') || 10
            ),
            watchOverflow: true,
            watchSlidesProgress: true,
            setWrapperSize: true,
            // Small mouse jitter still counts as a click, not a drag,
            // so the Thumbs module actually navigates the main slider.
            threshold: 8,
            shortSwipes: false,
            // NOTE: slideToClickedSlide intentionally NOT used here.
            // Thumb-click navigation is handled entirely by Swiper's
            // built-in Thumbs module (linked via slider's `thumbs.swiper`
            // option in initSwiper()). Do NOT also set slideToClickedSlide
            // or add a manual click listener on thumb slides — either one
            // will race against the Thumbs module and cause wrong active
            // thumbnails / clicks needing to be pressed multiple times.
            observer: true,
            observeParents: true,
            navigation: {
              nextEl: '[data-thumbs-next]',
              prevEl: '[data-thumbs-prev]'
            },
            direction: this.hasAttribute('data-thumbs-layout')
              ? 'vertical'
              : 'horizontal',
            breakpoints: {
              [this.breakpoints.desktop_large]: {
                spaceBetween: Number(
                  this.getAttribute('data-thumbs-gap-desktop') ||
                    this.getAttribute('data-thumbs-gap') ||
                    10
                )
              }
            }
          }
        }
      };
    }

    connectedCallback() {
      this.#applyThumbWidths();
      this.init();
      window.addEventListener(
        'resize',
        debounce(() => {
          this.initSwiper();
        }, 300)
      );
    }

    // Exposes the thumbnail width as CSS custom properties so it can be
    // set per-instance from theme/section settings via data attributes,
    // e.g. data-thumbs-width="75px" data-thumbs-width-desktop="90px".
    // Actual sizing lives entirely in CSS:
    //
    //   [data-thumbs] .swiper-slide { width: var(--thumb-width, 75px); }
    //   @media screen and (min-width: 990px) {
    //     [data-thumbs] .swiper-slide { width: var(--thumb-width-desktop, var(--thumb-width, 75px)); }
    //   }
    #applyThumbWidths() {
      const thumbsEl = this.settings.elements.thumbs;
      if (!thumbsEl) return;

      const mobileWidth = this.getAttribute('data-thumbs-width');
      const desktopWidth = this.getAttribute(
        'data-thumbs-width-desktop'
      );

      if (mobileWidth) {
        thumbsEl.style.setProperty('--thumb-width', mobileWidth);
      }
      if (desktopWidth) {
        thumbsEl.style.setProperty(
          '--thumb-width-desktop',
          desktopWidth
        );
      }
    }

    init() {
      if (typeof PhotoSwipeLightbox !== 'undefined') {
        const photoSwipeLightboxInstance = new PhotoSwipeLightbox({
          gallery: this,
          children: 'a[data-pswp-image]',
          pswpModule: PhotoSwipe
        });

        photoSwipeLightboxInstance.init();
      }

      this.initSwiper();
    }

    initSwiper() {
      if (
        window.innerWidth <= this.breakpoints.desktop ||
        !this.hasAttribute('data-slider-desktop-disabled')
      ) {
        if (this.swiperInitialized || !this.settings.elements.slider)
          return;

        this.swiperInitialized = true;

        if (this.settings.elements.thumbs) {
          this.settings.instances.thumbs = new Swiper(
            this.settings.elements.thumbs,
            this.settings.options.thumbs
          );

          // IMPORTANT: Use Swiper's built-in Thumbs module and ONLY this
          // mechanism for thumb <-> slider sync. Passing thumbs.swiper here
          // makes Swiper automatically:
          //   - handle thumbnail clicks and move the main slider
          //   - apply the correct 'swiper-slide-thumb-active' class
          //   - keep the thumbs viewport scrolled to the active thumb
          // We previously ALSO added a manual click handler on top of this
          // (#bindThumbClicks) — that caused two slideTo() calls to fire
          // per click, racing each other. That race is exactly what caused
          // the wrong thumbnail being marked active and the occasional lag.
          // Do NOT add a manual click handler alongside this — pick one.
          this.settings.options.slider.thumbs = {
            swiper: this.settings.instances.thumbs
          };
        }

        this.settings.instances.slider = new Swiper(
          this.settings.elements.slider,
          this.settings.options.slider
        );

        this.settings.instances.slider.on('slideChange', () => {
          window.pauseAllMedia();
          this.#syncStickyImage();
        });

        // Lazy images often get a real src only after the slide is visible.
        this.settings.instances.slider.on(
          'slideChangeTransitionEnd',
          () => {
            this.#syncStickyImage();
          }
        );

        // Re-measure once images finish loading (fixes mismeasured
        // slides on slow/lazy-loaded images).
        this.#updateOnImagesLoaded();
        this.#syncStickyImage();
      } else if (
        this.swiperInitialized &&
        this.hasAttribute('data-slider-desktop-disabled')
      ) {
        this.settings.instances.slider.destroy();
        if (this.settings.instances.thumbs) {
          this.settings.instances.thumbs.destroy();
        }
        this.swiperInitialized = false;
      }
    }

    // Copies the active main-gallery image into the sticky add-to-cart
    // bar. Off-screen slides use lazysizes (data-src, no src). Reading
    // img.src in that state returns the page URL, so the first click
    // looked like a no-op. Prefer currentSrc / src attr / data-src.
    #syncStickyImage(mediaEl) {
      const stickyImg = document.getElementById('pro-img');
      if (!stickyImg) return;

      const sourceSlide = this.#getActiveMediaSlide(mediaEl);
      if (!sourceSlide) return;

      const src = this.#getSlideImageSrc(sourceSlide);
      if (src) stickyImg.src = src;

      const pending = Array.from(
        sourceSlide.querySelectorAll('img')
      ).find(
        img =>
          !img.currentSrc &&
          !img.getAttribute('src') &&
          img.getAttribute('data-src')
      );

      if (pending) {
        const retry = () => this.#syncStickyImage(sourceSlide);
        pending.addEventListener('lazyloaded', retry, { once: true });
        pending.addEventListener('load', retry, { once: true });
      }
    }

    #getActiveMediaSlide(mediaEl) {
      if (mediaEl) return mediaEl;

      const activeFromDom = this.settings.elements.slider?.querySelector(
        '.swiper-slide-active'
      );
      if (activeFromDom) return activeFromDom;

      const slider = this.settings.instances.slider;
      if (slider && !slider.destroyed) {
        return slider.slides[slider.activeIndex];
      }

      return (
        this.querySelector(
          `${this.selectors.mediaItem}[data-selected]`
        ) ||
        this.querySelector(`${this.selectors.mediaItem}:not(.hidden)`)
      );
    }

    #getSlideImageSrc(sourceSlide) {
      const imgs = sourceSlide.querySelectorAll('img');
      for (const img of imgs) {
        const src =
          img.currentSrc ||
          img.getAttribute('src') ||
          img.getAttribute('data-src');
        if (src) return src;
      }
      return '';
    }

    // Forces a correct Swiper recalculation once every image inside the
    // gallery has actually finished loading, instead of relying on
    // whatever size was measured at init time (fixes slides being
    // mismeasured on slow/lazy-loaded images).
    #updateOnImagesLoaded() {
      const images = this.querySelectorAll('img');
      const pending = Array.from(images).filter(img => !img.complete);

      const update = () => {
        this.settings.instances.slider?.update();
        this.settings.instances.thumbs?.update();
      };

      if (pending.length === 0) {
        update();
        return;
      }

      let remaining = pending.length;
      const settle = () => {
        remaining -= 1;
        if (remaining === 0) update();
      };

      pending.forEach(img => {
        img.addEventListener('load', settle, { once: true });
        img.addEventListener('error', settle, { once: true });
      });
    }

    setActiveMedia(id) {
      const mediaFound = Array.from(
        this.querySelectorAll(this.selectors.mediaItem)
      ).find(media => Number(media.dataset.mediaId) === id);

      if (!mediaFound) return;
      if (
        !this.settings.instances.slider ||
        this.settings.instances.slider?.destroyed
      ) {
        const headerHeight =
          document.querySelector(this.selectors.header)
            ?.offsetHeight || 0;
        window.scroll({
          top:
            mediaFound.getBoundingClientRect().top +
            window.scrollY -
            headerHeight,
          behavior: 'smooth'
        });
        this.#syncStickyImage(mediaFound);
        return;
      }

      this.settings.instances.slider.slideTo(
        Number(mediaFound.dataset.index)
      );
    }

    filterMedia({ name, value }) {
      if (name !== this.filterMediaBy) return;

      const filteredMedia = this.querySelectorAll(
        `${this.selectors.mediaItem}[${this.selectors.mediaColor}="${value}"], ${this.selectors.thumbnail}[${this.selectors.mediaColor}="${value}"]`
      );
      const hasFilteredMedia = filteredMedia.length !== 0;

      this.querySelectorAll(
        `${this.selectors.mediaItem}, ${this.selectors.thumbnail}`
      ).forEach(media => {
        if (!hasFilteredMedia) {
          media.classList.add('swiper-slide');
          media.classList.remove('hidden');
        } else {
          media.classList.remove('swiper-slide');
          media.classList.add('hidden');
        }
      });

      const inactiveSliderInstance =
        !this.settings.instances.slider ||
        this.settings.instances.slider?.destroyed;

      if (!hasFilteredMedia && !inactiveSliderInstance) {
        this.settings.instances.slider.update();
        this.settings.instances.thumbs?.update();
        this.#syncStickyImage();
        return;
      }

      this.querySelectorAll(
        `${this.selectors.mediaItem}[${this.selectors.mediaColor}="${value}"], ${this.selectors.thumbnail}[${this.selectors.mediaColor}="${value}"]`
      ).forEach(media => {
        media.classList.add('swiper-slide');
        media.classList.remove('hidden');
      });

      if (inactiveSliderInstance) {
        this.#syncStickyImage();
        return;
      }

      this.settings.instances.slider.update();
      this.settings.instances.thumbs?.update();
      this.#syncStickyImage();
    }
  }

  customElements.define('product-media', ProductMedia);
}
